/**
 * @file reducer.ts
 * @description Payment context reducer for state management
 *
 * ## SECURITY INVARIANT: Backend-Only Payment Confirmation
 *
 * This reducer enforces a critical security invariant:
 *
 * > The UI state "payment confirmed" (UI_PAYMENT_NOT_REQUIRED with session !== null
 * > and currentStatus === 'CONFIRMED') can ONLY be set via a STATUS_UPDATE action
 * > that originates from a backend API response.
 *
 * ### Trust Boundaries
 * - **TRUSTED**: STATUS_UPDATE actions from paymentService.getPaymentStatus() (backend)
 * - **UNTRUSTED**: localStorage data, RESTORE_SESSION payloads, client state
 *
 * ### State Semantics for UI_PAYMENT_NOT_REQUIRED
 * - If `session === null`: IDLE state (no payment in progress)
 * - If `session !== null && currentStatus === 'CONFIRMED'`: PAID state (backend confirmed)
 *
 * ### Actions That Affect Payment State
 * | Action           | Can Set "PAID"? | Notes                              |
 * |------------------|-----------------|------------------------------------|
 * | STATUS_UPDATE    | YES (if CONFIRMED) | Only legitimate path to PAID    |
 * | RESTORE_SESSION  | NO              | Always sets PENDING, requires poll |
 * | CLEAR            | NO              | Returns to IDLE (session = null)   |
 * | INIT_*           | NO              | Sets IN_PROGRESS or FAILED         |
 *
 * @see .taskmaster/docs/research/task-35-payment-security-invariants.md
 */

import { PaymentUIState } from '../../types/payment';
import type { Payment } from '../../types/payment';
import type { PaymentAction, PaymentContextState, PaymentSession } from './types';

/**
 * Default initial state for PaymentContext.
 */
export const initialState: PaymentContextState = {
  session: null,
  paymentUIState: PaymentUIState.UI_PAYMENT_NOT_REQUIRED,
  isLoading: false,
  isPolling: false,
  currentStatus: null,
  error: null,
  pollCount: 0,
};

/**
 * Converts a Payment object from the API to a PaymentSession for the context.
 */
const paymentToSession = (payment: Payment): PaymentSession => ({
  paymentId: payment.id,
  tournamentId: payment.tournamentId,
  paymentType: payment.type,
  amount: payment.amount,
  currency: payment.currency,
  expiresAt: payment.expiresAt,
  providerData: payment.providerData,
});

/**
 * Maps backend PaymentStatus to frontend UI state.
 *
 * SECURITY: This function is the ONLY legitimate path to UI_PAYMENT_NOT_REQUIRED
 * with "paid" semantics. It should only be called from STATUS_UPDATE action
 * which receives status from backend API (paymentService.getPaymentStatus).
 *
 * @param status - PaymentStatus from backend API response
 * @returns Corresponding PaymentUIState for UI rendering
 */
const statusToUIState = (status: string): PaymentUIState => {
  switch (status) {
    case 'CONFIRMED':
      // SECURITY: Only 'CONFIRMED' from backend leads to "paid" UI state
      return PaymentUIState.UI_PAYMENT_NOT_REQUIRED;
    case 'EXPIRED':
      return PaymentUIState.UI_PAYMENT_EXPIRED;
    case 'FAILED':
    case 'REFUNDED':
    case 'CHARGEBACKED':
    case 'USED':
      return PaymentUIState.UI_PAYMENT_FAILED;
    case 'PENDING':
    default:
      return PaymentUIState.UI_PAYMENT_IN_PROGRESS;
  }
};

/**
 * Payment context reducer.
 * Handles all state transitions for payment operations.
 */
export const paymentReducer = (
  state: PaymentContextState,
  action: PaymentAction
): PaymentContextState => {
  switch (action.type) {
    case 'INIT_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        paymentUIState: PaymentUIState.UI_PAYMENT_IN_PROGRESS,
      };

    case 'INIT_SUCCESS':
      return {
        ...state,
        isLoading: false,
        session: paymentToSession(action.payload.payment),
        currentStatus: action.payload.payment.status,
        paymentUIState: PaymentUIState.UI_PAYMENT_IN_PROGRESS,
      };

    case 'INIT_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload.error,
        paymentUIState: PaymentUIState.UI_PAYMENT_FAILED,
      };

    case 'STATUS_UPDATE':
      // SECURITY: This is the ONLY action that can legitimately set UI_PAYMENT_NOT_REQUIRED
      // with "paid" semantics. The status MUST come from backend API (polling/getPaymentStatus).
      // Only 'CONFIRMED' status from backend leads to "payment complete" UI state.
      return {
        ...state,
        currentStatus: action.payload.status,
        paymentUIState: statusToUIState(action.payload.status),
        pollCount: state.pollCount + 1,
      };

    case 'POLL_START':
      return {
        ...state,
        isPolling: true,
        pollCount: 0,
      };

    case 'POLL_STOP':
      return {
        ...state,
        isPolling: false,
      };

    case 'CLEAR':
      // SECURITY: Returns to IDLE state (session = null). This is NOT "paid" state.
      // UI_PAYMENT_NOT_REQUIRED here means "no payment session active", not "payment confirmed".
      return initialState;

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload.error,
        paymentUIState: PaymentUIState.UI_PAYMENT_FAILED,
        isPolling: false,
      };

    case 'RESTORE_SESSION':
      // SECURITY INVARIANT: Restored sessions MUST re-verify with backend.
      // We ALWAYS set currentStatus to 'PENDING' regardless of any cached/stored status.
      // This forces polling and backend confirmation before reaching "paid" state.
      // RESTORE_SESSION can NEVER directly set UI_PAYMENT_NOT_REQUIRED (paid semantic).
      return {
        ...state,
        session: action.payload.session,
        paymentUIState: PaymentUIState.UI_PAYMENT_IN_PROGRESS,
        currentStatus: 'PENDING',
      };

    default:
      // Return unchanged state for unknown actions
      return state;
  }
};
